<template>
  <div class="map-container">
    <!-- 搜索框 -->
    <div class="search-box">
      <input
        v-model="searchKeyword"
        type="text"
        :placeholder="$t('footerAddress.inputPlaceholder')"
        @keyup.enter="searchPlace"
      />
      <button @click="searchPlace">{{$t('footerAddress.btnVal1')}}</button>
      <button @click="clearSearch">{{$t('footerAddress.btnVal2')}}</button>
    </div>

    <!-- 搜索结果列表 -->
    <div v-if="searchResults.length > 0" class="search-results">
      <ul>
        <li
          v-for="(result, index) in searchResults"
          :key="index"
          @click="selectPlace(result)"
        >
          <h4>{{ result.title }}</h4>
          <p>{{ result.address }}</p>
          <p v-if="result.phone">{{$t('footerAddress.btnVal3')}}：{{ result.phone }}</p>
        </li>
      </ul>
    </div>

    <!-- 百度地图 -->
    <baidu-map
      class="bm-view"
      :center="center"
      :zoom="zoom"
      :scroll-wheel-zoom="true"
      @ready="mapReady"
    >
      <!-- 定位控件 -->
      <bm-geolocation
        anchor="BMAP_ANCHOR_BOTTOM_RIGHT"
        :showAddressBar="true"
        :autoLocation="true"
      ></bm-geolocation>

      <!-- 缩放控件 -->
      <bm-navigation anchor="BMAP_ANCHOR_TOP_RIGHT"></bm-navigation>

      <!-- 比例尺 -->
      <bm-scale anchor="BMAP_ANCHOR_TOP_LEFT"></bm-scale>

      <!-- 地图类型控件 -->
      <bm-map-type
        :map-types="['BMAP_NORMAL_MAP', 'BMAP_HYBRID_MAP']"
        anchor="BMAP_ANCHOR_TOP_RIGHT"
      ></bm-map-type>

      <!-- 标记点 -->
      <bm-marker
        v-for="(marker, index) in markers"
        :key="index"
        :position="marker.position"
        :dragging="true"
        @click="markerClick(marker)"
        @dragend="markerDragend"
      >
        <!-- 信息窗口 -->
        <bm-info-window
          :show="marker.showInfo"
          @close="marker.showInfo = false"
          @open="marker.showInfo = true"
        >
          <div class="info-window">
            <h4>{{ marker.title }}</h4>
            <p>{{ marker.address }}</p>
            <p v-if="marker.phone">电话：{{ marker.phone }}</p>
            <button @click="setAsCenter(marker)">设为地图中心</button>
          </div>
        </bm-info-window>
      </bm-marker>

      <!-- 本地搜索 -->
      <bm-local-search
        v-if="map"
        :keyword="searchKeyword"
        :auto-viewport="true"
        :location="city"
        @searchcomplete="handleSearchComplete"
        @markersset="handleMarkersSet"
        style="display: none"
      ></bm-local-search>
    </baidu-map>

    <!-- 当前选中位置信息 -->
    <div v-if="selectedPlace" class="selected-info">
      <h3>已选择位置</h3>
      <p><strong>名称：</strong>{{ selectedPlace.title }}</p>
      <p><strong>地址：</strong>{{ selectedPlace.address }}</p>
      <p><strong>经纬度：</strong>{{ selectedPlace.point.lng }}, {{ selectedPlace.point.lat }}</p>
      <button @click="copyLocation">复制位置信息</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaiduMapSearch',
  data() {
    return {
      // 地图实例
      map: null,
      // 地图中心点
      center: { lng: 116.404, lat: 39.915 },
      // 缩放级别
      zoom: 12,
      // 搜索关键词
      searchKeyword: '',
      // 搜索结果
      searchResults: [],
      // 标记点数组
      markers: [],
      // 当前城市
      city: '北京',
      // 当前选中位置
      selectedPlace: null,
      // 本地搜索实例
      localSearch: null
    }
  },
  methods: {
    // 地图初始化完成
    mapReady({ BMap, map }) {
      this.map = map
      this.BMap = BMap
      console.log('地图初始化完成')
      
      // 获取当前位置
      this.getCurrentLocation()
    },

    // 获取当前位置
    getCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { longitude, latitude } = position.coords
            this.center = {
              lng: longitude,
              lat: latitude
            }
            this.zoom = 15
            
            // 反向地理编码获取地址
            this.reverseGeocode(longitude, latitude)
          },
          (error) => {
            console.error('获取位置失败:', error)
            // 使用默认位置
            this.center = { lng: 116.404, lat: 39.915 }
          }
        )
      }
    },

    // 反向地理编码
    reverseGeocode(lng, lat) {
      if (!this.map || !this.BMap) return
      
      const geocoder = new this.BMap.Geocoder()
      const point = new this.BMap.Point(lng, lat)
      
      geocoder.getLocation(point, (result) => {
        if (result) {
          this.city = result.addressComponents.city
          console.log('当前位置:', result.address)
        }
      })
    },

    // 搜索地点
    searchPlace() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }

      if (!this.map || !this.BMap) {
        this.$message.error('地图尚未初始化完成')
        return
      }

      // 创建本地搜索实例
      const local = new this.BMap.LocalSearch(this.map, {
        onSearchComplete: this.handleSearchComplete,
        renderOptions: {
          map: this.map,
          autoViewport: true
        }
      })

      local.search(this.searchKeyword)
    },

    // 搜索完成回调
    handleSearchComplete(results) {
      console.log('搜索结果:', results)
      
      this.searchResults = []
      this.markers = []
      
      if (results && results.getCurrentNumPois() > 0) {
        const pois = results.getCurrentNumPois()
        
        for (let i = 0; i < pois; i++) {
          const item = results.getPoi(i)
          
          // 添加到搜索结果列表
          this.searchResults.push({
            title: item.title,
            address: item.address,
            phone: item.phone,
            point: item.point,
            city: item.city
          })

          // 创建标记点
          this.markers.push({
            position: { lng: item.point.lng, lat: item.point.lat },
            title: item.title,
            address: item.address,
            phone: item.phone,
            showInfo: false
          })
        }
      } else {
        this.$message.warning('未找到相关地点')
      }
    },

    // 标记点设置完成
    handleMarkersSet(pois) {
      console.log('标记点已设置:', pois)
    },

    // 选择地点
    selectPlace(place) {
      return false
      this.selectedPlace = place
      
      // 移动地图中心到选择的位置
      this.center = {
        lng: place.point.lng,
        lat: place.point.lat
      }
      this.zoom = 16
      
      // 高亮显示对应标记点
      this.markers = this.markers.map(marker => {
        marker.showInfo = (
          marker.position.lng === place.point.lng && 
          marker.position.lat === place.point.lat
        )
        return marker
      })
      
      console.log('已选择位置:', place)
    },

    // 标记点点击事件
    markerClick(marker) {
      this.markers = this.markers.map(m => {
        m.showInfo = (m === marker)
        return m
      })
      
      this.selectedPlace = {
        title: marker.title,
        address: marker.address,
        phone: marker.phone,
        point: { lng: marker.position.lng, lat: marker.position.lat }
      }
    },

    // 标记点拖拽结束
    markerDragend(e) {
      const point = e.target.getPosition()
      console.log('新位置:', point.lng, point.lat)
      
      // 更新位置信息
      this.reverseGeocode(point.lng, point.lat)
    },

    // 设置为地图中心
    setAsCenter(marker) {
      this.center = marker.position
      this.zoom = 16
    },

    // 清除搜索
    clearSearch() {
      this.searchKeyword = ''
      this.searchResults = []
      this.markers = []
      this.selectedPlace = null
    },

    // 复制位置信息
    copyLocation() {
      if (!this.selectedPlace) return
      
      const text = `${this.selectedPlace.title}\n地址：${this.selectedPlace.address}\n经纬度：${this.selectedPlace.point.lng}, ${this.selectedPlace.point.lat}`
      
      navigator.clipboard.writeText(text)
        .then(() => {
          this.$message.success('位置信息已复制到剪贴板')
        })
        .catch(err => {
          console.error('复制失败:', err)
          this.$message.error('复制失败')
        })
    }
  },
  mounted() {
    // 组件挂载后可以初始化一些数据
  },
  beforeDestroy() {
    // 清理工作
    this.map = null
    this.BMap = null
  }
}
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: calc(100% - 120px);
}

.bm-view {
  width: 100%;
  height: 100%;
}

.search-box {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1000;
  background: white;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.search-box input {
  padding: 8px 12px;
  width: 300px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  margin-right: 10px;
}

.search-box button {
  padding: 8px 16px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 5px;
}

.search-box button:hover {
  background-color: #66b1ff;
}

.search-results {
  position: absolute;
  top: 80px;
  left: 20px;
  z-index: 1000;
  background: white;
  width: 400px;
  max-height: 400px;
  overflow-y: auto;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.search-results ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.search-results li {
  padding: 15px;
  border-bottom: 1px solid #e4e7ed;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-results li:hover {
  background-color: #f5f7fa;
}

.search-results h4 {
  margin: 0 0 5px 0;
  color: #303133;
}

.search-results p {
  margin: 0;
  color: #606266;
  font-size: 12px;
}

.selected-info {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  background: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  width: 300px;
}

.selected-info h3 {
  margin-top: 0;
  color: #303133;
}

.selected-info p {
  margin: 5px 0;
  color: #606266;
  font-size: 14px;
}

.selected-info button {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #67c23a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.selected-info button:hover {
  background-color: #85ce61;
}

.info-window {
  max-width: 200px;
}

.info-window h4 {
  margin: 0 0 5px 0;
  color: #303133;
}

.info-window p {
  margin: 0 0 5px 0;
  color: #606266;
  font-size: 12px;
}

.info-window button {
  padding: 4px 8px;
  font-size: 12px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 2px;
  cursor: pointer;
}
</style>